/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from 'react';
import useWorkspaceStore from '../../store/workspace/workspaceStore';
import { toast } from 'sonner';
import { useAIMemoryFieldDefinitions, useUpdateAIMemoryFieldDefinition } from '../../hooks/QueryHooks';
import type { AIMemoryObjectType, FieldDefinition } from '../../services/aiMemory/aiMemoryEndpoints.interface';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2 } from 'lucide-react';

interface FieldConfigurationModalProps {
  opened: boolean;
  onClose: () => void;
  objectId: string | null;
  objectType: AIMemoryObjectType | null;
}

const FieldConfigurationModal = ({ opened, onClose, objectId, objectType }: FieldConfigurationModalProps) => {
  const workspaceId = useWorkspaceStore((s: any) => s.workspace?.id);
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const { data, isLoading, isError, refetch, isFetching } = useAIMemoryFieldDefinitions({
    workspaceId,
    objectId: objectId || undefined,
    enabled: opened && !!workspaceId && !!objectId,
  } as any);

  const updateFieldMutation = useUpdateAIMemoryFieldDefinition();

  const fields: FieldDefinition[] = data?.data || [];

  const handleToggle = async (field: FieldDefinition, value: boolean) => {
    if (field.is_system_field) return;
    setPending((prev) => ({ ...prev, [field.id]: true }));
    try {
      await updateFieldMutation.mutateAsync({ workspace_id: workspaceId, field_id: field.id, data: { is_searchable: value } });
      toast.success('Updated', { description: `${field.display_name} ${value ? 'included' : 'excluded'} for insights` });
      await refetch();
    } catch (e) {
      toast.error('Update failed', { description: 'Could not update field settings' });
    } finally {
      setPending((prev) => ({ ...prev, [field.id]: false }));
    }
  };

  return (
    <Dialog open={opened} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Select Data Fields</DialogTitle>
          <DialogDescription>
            Choose which fields should power Conversation Intelligence. Changes may trigger re-indexing.
          </DialogDescription>
        </DialogHeader>

        {(isLoading || isFetching) ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : null}

        {isError ? (
          <Alert className="border-status-cancelled/20 bg-status-cancelled/5">
            <AlertDescription className="text-[12px] text-muted-foreground">
              Failed to load fields. Please try again.
            </AlertDescription>
          </Alert>
        ) : null}

        {!isLoading && fields?.length === 0 && !isError ? (
          <Alert className="border-border bg-muted/40">
            <AlertDescription className="text-[12px] text-muted-foreground">
              No fields found for this object.
            </AlertDescription>
          </Alert>
        ) : null}

        {fields && fields.length > 0 ? (
          <div className="max-h-[400px] overflow-auto rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-[11px]">Field</TableHead>
                  <TableHead className="text-[11px]">Column</TableHead>
                  <TableHead className="text-[11px]">Description</TableHead>
                  <TableHead className="text-[11px] text-right">Include in Insights</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fields.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] text-foreground">{f.display_name}</span>
                        {f.is_system_field ? (
                          <Badge variant="secondary" className="h-5 text-[10px]">system</Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <span className="text-[11px] text-muted-foreground">{f.column_name}</span>
                    </TableCell>
                    <TableCell className="py-2">
                      <span className="text-[11px] text-foreground">{f.description || '-'}</span>
                    </TableCell>
                    <TableCell className="py-2 text-right">
                      <Switch
                        checked={!!f.is_searchable}
                        disabled={f.is_system_field || !!pending[f.id]}
                        onCheckedChange={(checked) => { handleToggle(f, checked).catch(() => {}); }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" className="h-7 text-[12px]" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FieldConfigurationModal;
